import { QueryClient } from '@tanstack/react-query'
import { CATALOG_PAGE_COMPONENT_TYPES } from '../AppPage2/catalogComponentTypes'
import { TComponentType } from '../AppPage2/types'

export type ComponentContextSnapshot = {
  queryKey: unknown[]
  filters: Record<string, unknown>[]
  response: unknown
  dataUpdatedAt: number
}

const SUPPORTED_COMPONENT_TYPES = new Set<TComponentType>(CATALOG_PAGE_COMPONENT_TYPES)

function isRecordArray(value: unknown): value is Record<string, unknown>[] {
  return Array.isArray(value) && value.every((item) => item !== null && typeof item === 'object')
}

function extractFiltersFromQueryKey(queryKey: unknown[]): Record<string, unknown>[] {
  for (const part of queryKey) {
    if (isRecordArray(part)) {
      return part
    }
  }
  return []
}

export function isComponentTypeContextSelectable(componentType: TComponentType): boolean {
  return SUPPORTED_COMPONENT_TYPES.has(componentType)
}

export function findLatestComponentContextSnapshot({
  queryClient,
  componentId,
  componentType,
  appKey,
  projectId,
}: {
  queryClient: QueryClient
  componentId: string
  componentType: TComponentType
  appKey?: string
  projectId?: number
}): ComponentContextSnapshot | null {
  if (!isComponentTypeContextSelectable(componentType)) {
    return null
  }

  let latest: ComponentContextSnapshot | null = null
  const allQueries = queryClient.getQueryCache().getAll()

  for (const query of allQueries) {
    const { queryKey, state } = query
    if (!Array.isArray(queryKey)) continue
    if (queryKey[0] !== componentId || queryKey[1] !== componentType) continue
    if (appKey && !queryKey.some((item) => item === appKey)) continue
    if (typeof projectId === 'number' && !queryKey.some((item) => item === projectId)) continue
    if (state.status !== 'success') continue
    if (state.dataUpdatedAt <= 0 || state.data === undefined) continue

    if (!latest || state.dataUpdatedAt > latest.dataUpdatedAt) {
      latest = {
        queryKey: [...queryKey],
        filters: extractFiltersFromQueryKey(queryKey),
        response: state.data,
        dataUpdatedAt: state.dataUpdatedAt,
      }
    }
  }

  return latest
}
